import React, { Fragment, useState, useRef, useEffect } from 'react';
import styles from './styles.scss';
import classnames from 'classnames';
import Mousetrap from 'mousetrap';

import GlobalSearchStore from '../../rx-stores/global-search';
import useRxStore from '../../utils/use-rx-store';
import useRxDispatch from '../../utils/use-rx-dispatch';
import { GlobalSearchActionTypes } from '../../types/global-search';

import { ResourceStatus } from '../../types/resource';
import { SearchResult } from '../../types/global-search';

import {
  AppBarContext,
  AppBar,
  AppBarSection,
  Button,
  Icons,
} from '@densityco/ui';
import HorizontalForm from '../HorizontalForm';
import ErrorState from '../ErrorState';
import colorVariables from '@densityco/ui/variables/colors.json';

type ResourceType = {
  icon: React.FunctionComponent<any>;
  href: (n: SearchResult) => string;
};

const RESOURCE_TYPES: { [resourceName: string]: ResourceType } = {
  spaces: {
    icon: Icons.Space,
    href: i => `#/${i.result.organization_id}/spaces/${i.result.href_id}`,
  },
  units: {
    icon: Icons.Algo,
    href: i => `#/${i.result.organization_id}/sensors/${i.result.href_id}`,
  },
};

const GlobalSearchTextBox: React.FunctionComponent<{
  text: string;
  menuOpen: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  statusMessage: string;
  showClear: boolean;
  onClear: () => void;
}> = ({ text, menuOpen, onChange, statusMessage, showClear, onClear }) => {
  const input = useRef<HTMLInputElement>(null);

  // On initial mount or whenever the input ref changes, focus the text box
  useEffect(() => {
    if (input.current) {
      input.current.focus();
      input.current.scrollLeft = 99999999999999; // Always scroll all the way to the right
    }
  });

  return (
    <div
      className={classnames(styles.textbox, {
        [styles.empty]: text.length === 0,
        [styles.dialogExpanded]: menuOpen,
      })}
      onClick={() => input.current.focus()}
    >
      {text.length === 0 ? (
        <Icons.Search color={colorVariables.gray400} />
      ) : null}
      <div
        className={styles.autoExpandingTextbox}
        style={text.length === 0 ? { width: '100%' } : {}}
      >
        <span>{text}</span>
        <input
          type="text"
          placeholder="Serial number, doorway, or space"
          ref={input}
          value={text}
          onChange={onChange}
          data-test-id="global-search-input"
        />
      </div>

      <span className={styles.statusMessage} data-test-id="global-search-status-message">
        {statusMessage}
      </span>

      {showClear ? (
        <button className={styles.clearButton} onClick={() => onClear()} data-test-id="global-search-clear">
          <Icons.Close width={24} height={24} color="currentColor" />
        </button>
      ) : null}
    </div>
  );
};

const GlobalSearch: React.FunctionComponent<{}> = () => {
  const itemWrapper = useRef<HTMLDivElement>(null);

  const state = useRxStore(GlobalSearchStore);
  const dispatch = useRxDispatch();

  // On unmount, clear the global search contents
  useEffect(() => {
    return () => {
      dispatch({
        type: GlobalSearchActionTypes.GLOBAL_SEARCH_CLEAR_SEARCH_TEXT,
      });
    };
  }, []);

  // Escape can be pressed no matter the state of the resource
  useEffect(() => {
    const mousetrap = new Mousetrap(document.body);

    mousetrap.bind(['escape'], () => {
      const originalSearchText = state.searchText;
      if (originalSearchText.length > 0) {
        dispatch({
          type: GlobalSearchActionTypes.GLOBAL_SEARCH_CLEAR_SEARCH_TEXT,
        });
      } else {
        window.history.back();
      }
    });

    return () => mousetrap.reset();
  }, [state.searchText]);

  // Everything else, the resource must be loaded
  useEffect(() => {
    if (state.results.status !== ResourceStatus.COMPLETE) {
      return;
    }

    const mousetrap = new Mousetrap(document.body);

    mousetrap.bind(['enter'], openSelected);

    const scrollSelectedItemIntoView = index => {
      // If at the top, scroll to the top
      const itemWrapperScrollTop = itemWrapper.current.scrollTop;
      if (index === 0 && itemWrapperScrollTop !== 0) {
        itemWrapper.current.scrollTop = 0;
        return;
      }

      // Figure out where the currently selected item is in the search list
      const item = itemWrapper.current.querySelector('[data-selected]');
      const itemWrapperBoundingBox = itemWrapper.current.getBoundingClientRect();
      const itemBoundingBox = item.getBoundingClientRect();
      const itemPosition = itemBoundingBox.top - itemWrapperBoundingBox.top;

      // Handle the top case
      if (itemPosition < 0) {
        itemWrapper.current.scrollTop += itemPosition;
        return;
      }

      // Handle the bottom case
      const itemWrapperScrollBottom =
        itemWrapperBoundingBox.height - itemBoundingBox.height;
      if (itemPosition > itemWrapperScrollBottom) {
        const amountToScroll = itemPosition - itemWrapperScrollBottom;
        itemWrapper.current.scrollTop += amountToScroll;
        return;
      }
    };

    mousetrap.bind(['up', 'ctrl+k', 'ctrl+p', 'shift+tab'], e => {
      e.preventDefault();
      const newIndex = state.selectedIndex <= 0 ? 0 : state.selectedIndex - 1;
      dispatch({
        type: GlobalSearchActionTypes.GLOBAL_SEARCH_SET_SELECTED_INDEX,
        index: newIndex,
      });
      scrollSelectedItemIntoView(newIndex);
    });

    mousetrap.bind(['down', 'ctrl+j', 'ctrl+n', 'tab'], e => {
      e.preventDefault();
      const newIndex =
        state.selectedIndex >= lastGroupStartIndex - 1
          ? lastGroupStartIndex - 1
          : state.selectedIndex + 1;
      dispatch({
        type: GlobalSearchActionTypes.GLOBAL_SEARCH_SET_SELECTED_INDEX,
        index: newIndex,
      });
      scrollSelectedItemIntoView(newIndex);
    });

    return () => mousetrap.reset();
  }, [state]);

  const openSelected = (e: { ctrlKey: boolean; metaKey: boolean }) => {
    const href = hrefsAtIndex[state.selectedIndex];

    // When holding down ctrl / command, open in a new tab (just like clicking)
    if (e.ctrlKey || e.metaKey) {
      window.open(href, '_blank');
    } else {
      // Clear the state if not opening in a new tab
      dispatch({
        type: GlobalSearchActionTypes.GLOBAL_SEARCH_CLEAR_SEARCH_TEXT,
      });
      window.location.href = href;
    }
  };

  const keyboardShortcut = navigator.userAgent.includes('Mac') ? '\u2318K' : 'ctrl+k';

  let menuOpen = false;
  let statusMessage = null;
  let items = null;
  let lastGroupStartIndex = 0;
  let error = null;
  const hrefsAtIndex = [];
  switch (state.results.status) {
    case ResourceStatus.ERROR:
      error = <ErrorState error={state.results.error} />;
      statusMessage = '- No results found';
      break;
    case ResourceStatus.COMPLETE:
      const resourceGroups: {
        [key: string]: Array<SearchResult>;
      } = state.results.data.reduce(
        (groups, item) => ({
          ...groups,
          [item.resource_name]: [...(groups[item.resource_name] || []), item],
        }),
        {},
      );

      items = Object.entries(resourceGroups).map(([resourceName, results]) => {
        const jsx = (
          <Fragment key={resourceName}>
            <div className={styles.header}>
              Search results for {resourceName}: {results.length}
            </div>
            <table className={styles.list}>
              <tbody>
                {results.map((item, i) => {
                  const index = lastGroupStartIndex + i;
                  const resource = RESOURCE_TYPES[resourceName];
                  hrefsAtIndex.push(resource.href(item));
                  return (
                    <tr
                      key={item.result.id}
                      className={classnames({
                        [styles.selected]: index === state.selectedIndex,
                      })}
                      data-selected={
                        index === state.selectedIndex ? true : undefined
                      }
                      onMouseEnter={() => {
                        dispatch({
                          type:
                            GlobalSearchActionTypes.GLOBAL_SEARCH_SET_SELECTED_INDEX,
                          index,
                        });
                      }}
                      onFocus={e => {
                        dispatch({
                          type:
                            GlobalSearchActionTypes.GLOBAL_SEARCH_SET_SELECTED_INDEX,
                          index,
                        });
                      }}
                      onClick={openSelected}
                      data-test-id="global-search-result"
                      data-test-item-id={item.result.id}
                    >
                      <td className={styles.cellName}>
                        <span
                          style={{
                            display: 'inline-block',
                            transform: `translateY(2px)`,
                            paddingRight: 8,
                          }}
                        >
                          <resource.icon
                            width={16}
                            height={16}
                            color={colorVariables.gray500}
                          />
                        </span>
                        {item.result.name}
                      </td>
                      <td className={styles.cellOrganization}>
                        {item.result.organization_name}
                      </td>
                      <td className={styles.cellDoorway}>
                        {'doorway_name' in item.result &&
                        item.result.doorway_name ? (
                          <Fragment>
                            <span className={styles.cellDoorwayIcon}>
                              <Icons.Doorway
                                width={16}
                                height={16}
                                color={colorVariables.gray700}
                              />
                            </span>
                            {item.result.doorway_name}
                          </Fragment>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Fragment>
        );

        lastGroupStartIndex += results.length;
        return jsx;
      });

      if (items.length === 0) {
        menuOpen = false;
        statusMessage = '- No results found';
      } else {
        menuOpen = true;
      }
      break;

    case ResourceStatus.LOADING:
      statusMessage = '- Loading';
      break;
  }

  return (
    <div className={styles.wrapper}>
      <div
        className={classnames(styles.dialog, {
          [styles.dropshadow]: menuOpen,
        })}
      >
        <GlobalSearchTextBox
          text={state.searchText}
          menuOpen={menuOpen}
          onChange={e =>
            dispatch({
              type: GlobalSearchActionTypes.GLOBAL_SEARCH_SET_SEARCH_TEXT,
              text: e.target.value,
            })
          }
          statusMessage={statusMessage}
          showClear={state.results.status !== ResourceStatus.IDLE}
          onClear={() =>
            dispatch({
              type: GlobalSearchActionTypes.GLOBAL_SEARCH_CLEAR_SEARCH_TEXT,
            })
          }
        />

        {menuOpen ? (
          <Fragment>
            <div className={styles.dialogScroll} ref={itemWrapper} data-test-id="global-search-dialog-scroll">
              {items}
            </div>
            <AppBarContext.Provider value="BOTTOM_ACTIONS">
              <AppBar>
                <AppBarSection />
                <AppBarSection>
                  <HorizontalForm>
                    <Button
                      variant="underline"
                      onClick={() =>
                        dispatch({
                          type:
                            GlobalSearchActionTypes.GLOBAL_SEARCH_CLEAR_SEARCH_TEXT,
                        })
                      }
                      data-test-id="global-search-cancel"
                    >
                      Cancel
                    </Button>
                  </HorizontalForm>
                </AppBarSection>
              </AppBar>
            </AppBarContext.Provider>
          </Fragment>
        ) : null}
      </div>

      {error}

      {!menuOpen ? (
        <span className={styles.openHint}>
          Hint: Open faster by pressing {keyboardShortcut}
        </span>
      ) : null}
    </div>
  );
};
export default GlobalSearch;
